// @tennis-matches/core/utils
// Shared schedule generation (Web + Mobile).
// Migrated from packages/core/src/schedule.js

import { appendKoRounds } from "../ko/index.js";

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pairKey(a, b) {
  const x = Math.min(a, b);
  const y = Math.max(a, b);
  return `${x}-${y}`;
}

function makeMatchId(r, m) {
  // stable & UI-friendly
  return `r${r + 1}m${m + 1}`;
}

/**
 * Prüft, ob zwei Spieler (Indizes/Keys) als Partner ausgeschlossen sind.
 * @param {Record<string,number[]>} [excludesByPlayer] Spieler-Key → ausgeschlossene Partner-Indizes
 * @param {number|string} a Spieler A
 * @param {number|string} b Spieler B
 * @returns {boolean}
 */
export function isExcluded(excludesByPlayer, a, b) {
  if (!excludesByPlayer || typeof excludesByPlayer !== "object") return false;
  const exA = excludesByPlayer[String(a)];
  const exB = excludesByPlayer[String(b)];
  if (Array.isArray(exA) && exA.includes(Number(b))) return true;
  if (Array.isArray(exB) && exB.includes(Number(a))) return true;
  return false;
}

/**
 * Empfohlene Rundenanzahl für nicht-feste Doppel (Jeder mit jedem gegen jeden).
 * Genug Runden für ein sinnvolles Ranking, ohne zu viele Runden (z. B. nicht 15 bei 20 Spielern).
 * @param {number} playerCount - Anzahl Spieler (gerade empfohlen)
 * @returns {number} Vorschlag 3–8 Runden
 */
export function getOptimalRoundsForFlexibleDoubles(playerCount) {
  const n = Math.max(2, Number(playerCount) || 2);
  const minRounds = 3;
  const maxRounds = 8;
  const suggested = Math.ceil(n / 2);
  return Math.max(minRounds, Math.min(maxRounds, suggested));
}

/**
 * LK-Aufstellung für Feste Doppel: Obere Hälfte (nach LK sortiert) bekommt Partner
 * per Zufall aus der unteren Hälfte. Spieler mit bekannter LK in der oberen Hälfte
 * sind untereinander ausgeschlossen.
 *
 * @param {number[]} players - Spieler-Indizes (1-basiert)
 * @param {Record<string,number>} [lkByPlayer] - LK pro Spieler (kleiner = besser)
 * @param {Record<string,number[]>} [excludesByPlayer] - Zusätzliche Ausschlüsse
 * @returns {Array<[number,number]>} Feste Teams [[a,b],[c,d],...]
 */
export function buildLkBasedFixedPairs(players, lkByPlayer, excludesByPlayer) {
  if (!Array.isArray(players) || players.length < 2) return [];
  const lk = lkByPlayer && typeof lkByPlayer === "object" ? lkByPlayer : {};
  const n = players.length;
  const half = Math.floor(n / 2);

  // Sortieren: nach LK aufsteigend, Spieler ohne LK ans Ende (Index als Tiebreaker)
  const sorted = players.slice().sort((a, b) => {
    const lkA = Number(lk[String(a)]);
    const lkB = Number(lk[String(b)]);
    const hasA = Number.isFinite(lkA);
    const hasB = Number.isFinite(lkB);
    if (hasA && hasB) return lkA - lkB;
    if (hasA) return -1;
    if (hasB) return 1;
    return a - b;
  });

  const upper = sorted.slice(0, half);
  const lower = sorted.slice(half);
  const lowerShuffled = shuffle(lower);
  const used = new Set();
  const pairs = [];

  for (const u of upper) {
    // Erst versuchen: Partner der nicht ausgeschlossen ist
    let found = -1;
    for (let i = 0; i < lowerShuffled.length; i++) {
      if (used.has(lowerShuffled[i])) continue;
      if (isExcluded(excludesByPlayer, u, lowerShuffled[i])) continue;
      found = i;
      break;
    }
    // Fallback: ersten unvergebenen Partner (auch wenn ausgeschlossen)
    if (found < 0) {
      for (let i = 0; i < lowerShuffled.length; i++) {
        if (used.has(lowerShuffled[i])) continue;
        found = i;
        break;
      }
    }
    if (found < 0) continue;
    const partner = lowerShuffled[found];
    used.add(partner);
    pairs.push([Math.min(u, partner), Math.max(u, partner)]);
  }

  return pairs;
}

/**
 * Prüft, ob bei LK-Aufstellung Spieler ohne LK vorhanden sind (Warnung).
 * @param {number[]} players - Spieler-Indizes
 * @param {Record<string,number>} [lkByPlayer] - LK pro Spieler
 * @returns {boolean} true wenn mindestens ein Spieler keine LK hat
 */
export function hasMissingLkForLkBased(players, lkByPlayer) {
  if (!Array.isArray(players) || players.length === 0) return false;
  const lk = lkByPlayer && typeof lkByPlayer === "object" ? lkByPlayer : {};
  return players.some((p) => !Number.isFinite(Number(lk[String(p)])));
}

/**
 * Build a Schleifchen-style schedule.
 *
 * @param {object} args
 * @param {number} args.playerCount
 * @param {number} args.rounds
 * @param {"singles"|"doubles"} args.format
 * @param {number[]} [args.courtsNumbers]
 * @param {string} [args.teamAllocationMode] "manual" | "random" | "lkBased"
 * @param {Array<[number,number]>} [args.manualTeams] Feste Doppel-Paare (nur bei manual)
 * @param {Record<string,number>} [args.lkByPlayer] LK pro Spieler-Index (nur bei lkBased)
 * @param {Record<string,number[]>} [args.excludesByPlayer] Ausschlüsse: Spieler-Key → Indizes, die nicht Partner sein dürfen
 * @returns {Array<Array<object>>} rounds -> matches
 */
export function buildSchedule({
  playerCount,
  rounds,
  format,
  courtsNumbers,
  teamAllocationMode,
  manualTeams,
  lkByPlayer,
  excludesByPlayer,
}) {
  const players = Array.from({ length: playerCount }, (_, i) => i + 1);
  const partnerUsed = new Set();
  const opponentUsed = new Set();
  const schedule = [];

  const courts =
    Array.isArray(courtsNumbers) && courtsNumbers.length
      ? courtsNumbers.map((x) => Number(x)).filter((n) => Number.isFinite(n))
      : [];

  const playerSet = new Set(players);
  const mode = String(teamAllocationMode || "").toLowerCase();
  const formatNorm = String(format || "").trim();
  const isFixedDoublesFormat = formatNorm === "fixedDoubles" || formatNorm.toLowerCase() === "fixeddoubles" || formatNorm === "fixed-doppel";

  // Feste Doppel: Teams bleiben über alle Runden konstant (manual, lkBased, oder random bei fixedDoubles)
  const useFixedPairs =
    players.length >= 2 &&
    (isFixedDoublesFormat || (format !== "singles" && (mode === "manual" || mode === "lbased")));

  let fixedPairs = [];
  if (useFixedPairs && mode === "manual" && Array.isArray(manualTeams) && manualTeams.length > 0) {
    fixedPairs = manualTeams
      .map((p) => (Array.isArray(p) ? [Number(p[0]), Number(p[1])] : []))
      .filter(([a, b]) => playerSet.has(a) && playerSet.has(b) && a !== b)
      .map(([a, b]) => (a < b ? [a, b] : [b, a]));
    // Dedupe und Reihenfolge stabil halten
    const seen = new Set();
    fixedPairs = fixedPairs.filter((pair) => {
      const key = `${pair[0]}-${pair[1]}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } else if (useFixedPairs && mode === "lbased") {
    fixedPairs = buildLkBasedFixedPairs(players, lkByPlayer, excludesByPlayer);
  }
  if (useFixedPairs && isFixedDoublesFormat && fixedPairs.length === 0) {
    const shuffled = shuffle(players.slice());
    for (let i = 0; i < shuffled.length - 1; i += 2) {
      const a = shuffled[i];
      const b = shuffled[i + 1];
      if (a != null && b != null) fixedPairs.push([Math.min(a, b), Math.max(a, b)]);
    }
  }

  const hasFixedPairs = fixedPairs.length > 0;
  const unpairedFromFixed = hasFixedPairs
    ? players.filter((p) => !fixedPairs.some(([a, b]) => a === p || b === p))
    : [];

  if (useFixedPairs && hasFixedPairs) {
    // Runden: Paare durchmischen, dann Paar vs Paar (evtl. Rest als Sitout)
    const pairsForRounds = fixedPairs.slice();
    const extraPairs = [];
    if (unpairedFromFixed.length >= 2) {
      const rest = shuffle(unpairedFromFixed);
      for (let i = 0; i < rest.length - 1; i += 2) {
        extraPairs.push([rest[i], rest[i + 1]].sort((x, y) => x - y));
      }
    }
    const allPairs = [...pairsForRounds, ...extraPairs];

    for (let r = 0; r < rounds; r++) {
      const roundPairs = shuffle(allPairs.slice());
      const matches = [];
      for (let i = 0; i < roundPairs.length; i += 2) {
        if (i + 1 >= roundPairs.length) {
          matches.push({ type: "sitout", players: roundPairs[i] });
        } else {
          const A = roundPairs[i];
          const B = roundPairs[i + 1];
          matches.push({ type: "doubles", A, B });
        }
      }
      matches.forEach((m, idx) => {
        if (m.type === "doubles") {
          m.id = makeMatchId(r, idx);
          if (courts.length && Number.isFinite(courts[idx % courts.length])) m.court = courts[idx % courts.length];
        }
      });
      schedule.push(matches);
    }
    return schedule;
  }

  for (let r = 0; r < rounds; r++) {
    let pool = shuffle(players);
    const used = new Set();
    const matches = [];

    function takeNext() {
      for (let i = 0; i < pool.length; i++) {
        const p = pool[i];
        if (!used.has(p)) {
          used.add(p);
          pool.splice(i, 1);
          return p;
        }
      }
      return null;
    }

    if (format === "singles") {
      while (true) {
        const a = takeNext();
        if (!a) break;

        let bestIdx = -1;
        let bestScore = -1;
        for (let i = 0; i < pool.length; i++) {
          const b = pool[i];
          if (used.has(b)) continue;
          const ok = !opponentUsed.has(pairKey(a, b));
          const score = (ok ? 2 : 0) + Math.random();
          if (score > bestScore) {
            bestScore = score;
            bestIdx = i;
          }
        }
        if (bestIdx === -1) {
          matches.push({ type: "bye", players: [a] });
          continue;
        }

        const b = pool.splice(bestIdx, 1)[0];
        used.add(b);
        opponentUsed.add(pairKey(a, b));
        matches.push({ type: "singles", A: [a], B: [b] });
      }
    } else {
      // Doubles: optimal mixing
      // Goal: minimize partner & opponent repeats
      while (pool.length > 0) {
        const a = takeNext();
        if (!a) break;

        if (pool.length < 3) {
          const rest = [a, ...pool.splice(0, pool.length)];
          matches.push({ type: "sitout", players: rest });
          break;
        }

        let bestGroup = null;
        let bestMatch = null;
        let bestScore = -1;

        const n = pool.length;
        for (let i = 0; i < n - 2; i++) {
          const b = pool[i];
          for (let j = i + 1; j < n - 1; j++) {
            const c = pool[j];
            for (let k = j + 1; k < n; k++) {
              const d = pool[k];

              const opts = [
                { A: [a, b], B: [c, d] },
                { A: [a, c], B: [b, d] },
                { A: [a, d], B: [b, c] },
              ];

              for (const o of opts) {
                if (isExcluded(excludesByPlayer, a, o.A[1])) continue;
                const pA = partnerUsed.has(pairKey(o.A[0], o.A[1])) ? 0 : 6;
                const pB = partnerUsed.has(pairKey(o.B[0], o.B[1])) ? 0 : 6;

                let opp = 0;
                for (const pa of o.A) for (const pb of o.B) if (!opponentUsed.has(pairKey(pa, pb))) opp++;

                const score = pA + pB + opp + Math.random();
                if (score > bestScore) {
                  bestScore = score;
                  bestGroup = [b, c, d];
                  bestMatch = o;
                }
              }
            }
          }
        }

        if (!bestMatch || !bestGroup) {
          const b = pool.shift();
          const c = pool.shift();
          const d = pool.shift();
          bestMatch = { A: [a, b], B: [c, d] };
          bestGroup = [b, c, d];
        } else {
          for (const p2 of bestGroup) {
            const idx = pool.indexOf(p2);
            if (idx >= 0) pool.splice(idx, 1);
          }
        }

        partnerUsed.add(pairKey(bestMatch.A[0], bestMatch.A[1]));
        partnerUsed.add(pairKey(bestMatch.B[0], bestMatch.B[1]));
        for (const pa of bestMatch.A) for (const pb of bestMatch.B) opponentUsed.add(pairKey(pa, pb));

        matches.push({ type: "doubles", A: bestMatch.A, B: bestMatch.B });
      }
    }

    // IDs + Courts (only for real matches)
    matches.forEach((m, idx) => {
      if (m.type === "singles" || m.type === "doubles") {
        m.id = makeMatchId(r, idx);

        if (courts.length) {
          const c = courts[idx % courts.length];
          if (Number.isFinite(c)) m.court = c;
        }
      }
    });

    schedule.push(matches);
  }

  return schedule;
}

/**
 * Round-robin schedule for N teams: (N-1) rounds, each team plays every other once.
 * Teams are indices 0..n-1. Returns rounds of { home, away } team indices.
 * @param {number} nTeams
 * @returns {{ round: number, pairs: Array<{ home: number, away: number }> }[]}
 */
function roundRobinPairs(nTeams) {
  const n = Math.max(2, Number(nTeams) || 2);
  const rounds = [];
  const fixed = n - 1; // last team fixed
  for (let round = 0; round < n - 1; round++) {
    const pairs = [];
    pairs.push({ home: fixed, away: (round + 0) % (n - 1) });
    for (let i = 1; i < n / 2; i++) {
      const home = (round + i) % (n - 1);
      const away = (n - 1 - i + round) % (n - 1);
      pairs.push({ home, away });
    }
    rounds.push({ round: round + 1, pairs });
  }
  return rounds;
}

/**
 * Build Playoffs phase-1 schedule: Round Robin (jeder gegen jeden) for doubles.
 * Teams are formed from players (pairs). Same team allocation as buildSchedule.
 *
 * @param {object} args
 * @param {number} args.playerCount
 * @param {number} args.vorrundenCount - number of round-robin rounds (1..teamCount-1)
 * @param {4|6|8} [args.koTeilnehmer] - how many qualify for KO
 * @param {boolean} [args.playThirdPlace] - if true, add Platz 3 match (default: false)
 * @param {"singles"|"doubles"} [args.format]
 * @param {number[]} [args.courtsNumbers]
 * @param {string} [args.teamAllocationMode]
 * @param {Array<[number,number]>} [args.manualTeams]
 * @param {Record<string,number>} [args.lkByPlayer]
 * @param {Record<string,number[]>} [args.excludesByPlayer] Ausschlüsse: Spieler-Key → Indizes, die nicht Partner sein dürfen
 * @returns {Array<Array<object>>} rounds -> matches (same shape as buildSchedule)
 */
export function buildPlayoffsSchedule({
  playerCount,
  vorrundenCount,
  koTeilnehmer = 4,
  playThirdPlace = false,
  format = "doubles",
  courtsNumbers,
  teamAllocationMode,
  manualTeams,
  lkByPlayer,
  excludesByPlayer,
}) {
  const players = Array.from({ length: playerCount }, (_, i) => i + 1);
  const playerSet = new Set(players);
  const mode = String(teamAllocationMode || "").toLowerCase();
  const formatNorm = String(format || "").trim();
  const isFixedDoublesFormat = formatNorm === "fixedDoubles" || formatNorm.toLowerCase() === "fixeddoubles" || formatNorm === "fixed-doppel";

  /** Paare bilden unter Beachtung von excludesByPlayer (greedy). */
  function formPairsRespectingExclusions(pool) {
    const remaining = pool.slice();
    const pairs = [];
    while (remaining.length >= 2) {
      const a = remaining.shift();
      let found = -1;
      for (let i = 0; i < remaining.length; i++) {
        if (!isExcluded(excludesByPlayer, a, remaining[i])) {
          found = i;
          break;
        }
      }
      if (found === -1) continue;
      const b = remaining.splice(found, 1)[0];
      pairs.push([a, b].sort((x, y) => x - y));
    }
    return pairs;
  }  // Form teams (pairs for doubles) – bei Feste Doppel bleiben Teams über Vorrunde + K.O. konstant
  let teamPairs = [];
  if (
    players.length >= 2 &&
    (isFixedDoublesFormat || (format !== "singles" && (mode === "manual" || mode === "lbased")))
  ) {
    if (mode === "manual" && Array.isArray(manualTeams) && manualTeams.length > 0) {
      teamPairs = manualTeams
        .map((p) => (Array.isArray(p) ? [Number(p[0]), Number(p[1])] : []))
        .filter(([a, b]) => playerSet.has(a) && playerSet.has(b) && a !== b && !isExcluded(excludesByPlayer, a, b))
        .map(([a, b]) => (a < b ? [a, b] : [b, a]));
      const seen = new Set();
      teamPairs = teamPairs.filter((pair) => {
        const key = `${pair[0]}-${pair[1]}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } else if (mode === "lbased") {
      teamPairs = buildLkBasedFixedPairs(players, lkByPlayer, excludesByPlayer);
    }
  }
  if (teamPairs.length === 0 && isFixedDoublesFormat) {
    const shuffled = shuffle(players.slice());
    for (let i = 0; i < shuffled.length - 1; i += 2) {
      const a = shuffled[i];
      const b = shuffled[i + 1];
      if (a != null && b != null) teamPairs.push([Math.min(a, b), Math.max(a, b)]);
    }
  }
  if (teamPairs.length === 0) {
    // Random pairs, respecting exclusions
    const shuffled = shuffle(players.slice());
    teamPairs = formPairsRespectingExclusions(shuffled);
  }

  const nTeams = teamPairs.length;
  if (nTeams < 2) return [];

  const rr = roundRobinPairs(nTeams);
  // Gewünschte Rundenanzahl aus Setup übernehmen; bei Bedarf Round-Robin wiederholen (r % rr.length)
  const numRounds = Math.max(1, Math.min(99, Math.trunc(Number(vorrundenCount) || rr.length)));
  const courts = Array.isArray(courtsNumbers) && courtsNumbers.length
    ? courtsNumbers.map(Number).filter(Number.isFinite)
    : [];  const schedule = [];
  for (let r = 0; r < numRounds; r++) {
    const roundPairs = rr[r % rr.length].pairs;
    const matches = roundPairs.map(({ home, away }, idx) => {
      const A = teamPairs[home];
      const B = teamPairs[away];
      const match = {
        type: "doubles",
        A: A.slice(),
        B: B.slice(),
        id: makeMatchId(r, idx),
      };
      if (courts.length && courts[idx % courts.length] != null) {
        match.court = courts[idx % courts.length];
      }
      return match;
    });
    schedule.push(matches);
  }

  // K.O.-Phase: VF/HF/Finale (+ optional Platz 3)
  if (koTeilnehmer >= 2 && koTeilnehmer <= 8) {
    appendKoRounds(schedule, koTeilnehmer, courts, { playThirdPlace });
  }
  return schedule;
}/**
 * Anzahl K.O.-Runden je nach Teilnehmerzahl (Teams).
 * Finale + Platz 3 zählen als 1 Runde (werden zusammen gespielt).
 *
 * Top 2 → 1 Runde (Finale)
 * Top 4 → 2 Runden (HF, Finale+Platz3)
 * Top 6 → 2 Runden (Runde 6: HF+Platz5/6, Runde 7: Finale+Platz3)
 * Top 8 → 3 Runden (VF, HF, Finale+Platz3)
 *
 * @param {number} [koTeilnehmer] - Anzahl K.O.-Teams (2, 4, 6 oder 8; sonst 4)
 * @returns {number}
 */
export function getPlayoffKoRoundsCount(koTeilnehmer) {
  const k = Number(koTeilnehmer);
  const n = [2, 4, 6, 8].includes(k) ? k : 4;
  if (n === 2) return 1;
  if (n === 4 || n === 6) return 2;
  if (n === 8) return 3;
  return 2;
}/**
 * Wenn ein Playoff-Turnier nur Vorrunden im Schedule hat, werden K.O.-Runden angehängt.
 * @param {Array<Array<object>>} schedule - Schedule (nur Vorrunde)
 * @param {number} vorrundenCount - Erwartete Vorrunden-Anzahl
 * @param {2|4|6|8} koTeilnehmer - K.O.-Teilnehmer
 * @param {{ playThirdPlace?: boolean }} [options] - playThirdPlace (default: false)
 * @returns {Array<Array<object>>} Schedule mit angehängten K.O.-Runden
 */
export function ensurePlayoffKoRounds(schedule, vorrundenCount, koTeilnehmer, options = {}) {
  if (!Array.isArray(schedule) || schedule.length !== vorrundenCount) return schedule;
  if (![2, 4, 6, 8].includes(Number(koTeilnehmer))) return schedule;
  const copy = schedule.slice();
  appendKoRounds(copy, Number(koTeilnehmer), [], options);
  return copy;
}
